<script setup lang="ts">
import type { Navigation, PagesProjects } from '~/types';
import { getPublishedNavigation } from '~/utils/navigation';
import { getPortfolioTourLink } from '~/utils/portfolio-tour-link';

const { data: globals } = await useGlobals();
const title = computed(() => globals.value?.title ?? 'BVB General Contractors');

const config = useRuntimeConfig();
const route = useRoute();
const previewEnabled = computed(() => route.query.preview === 'true' && route.query.version === 'virtual-tour'
  && (config.public.nodeEnv === 'development' || config.public.nuxtEnv === 'staging'));
const { data: previewPage } = await useAsyncData('portfolioNavigationPreview', async () => {
  if (!previewEnabled.value) return null;
  return await $fetch<PagesProjects>('/api/portfolio-page?preview=true&version=virtual-tour');
}, { watch: [previewEnabled] });

const {
  data: navigation,
} = await useAsyncData(
  'mainNavigation',
  () => {
    return useDirectusProxy<Navigation[]>({
      operation: 'readItems',
      collection: 'navigation',
      params: {
        filter: {
          title: {
            _eq: 'Main',
          },
        },
        fields: [
          'id', 'status',
          {
            items: [
              'id',
              'status', 'parent', 'sort', 'open_in_new_tab',
              'has_children',
              'title',
              'icon',
              'label',
              'type',
              'url',
              {
                page: ['permalink', 'title'],
                children: [
                  'id',
                  'status', 'sort', 'open_in_new_tab',
                  'title',
                  'has_children',
                  'icon',
                  'label',
                  'type',
                  'url',
                  {
                    page: ['permalink', 'title'],
                  },
                ],
              },
            ],
          },
        ],
      },
    });
  },
  {
    transform: (data) => getPublishedNavigation(data[0]),
  },
);
const displayedNavigation = computed(() => {
  const menu = navigation.value;
  const link = previewEnabled.value && previewPage.value?.is_virtual_tour_preview
    ? getPortfolioTourLink(previewPage.value) : null;
  if (!menu || !link) return menu;
  const tourTarget = new URL(link.href, 'https://preview.invalid');
  tourTarget.searchParams.set('version', 'virtual-tour');
  const tourHref = tourTarget.pathname + tourTarget.search + tourTarget.hash;
  return {
    ...menu,
    items: menu.items.map((item) => {
      if (item.id !== 'eea8c990-f178-45d3-9be4-8a157faa4136') return item;
      return { ...item, has_children: true, children: [
        { ...item, id: `${item.id}-preview-all`, title: 'All Projects', type: 'url' as const,
          icon: 'grid_view', label: 'Browse our completed construction projects.',
          url: '/projects?preview=true&version=virtual-tour', page: null, has_children: false, children: [] },
        { ...item, id: `${item.id}-preview-tour`, title: link.label, type: 'url' as const,
          icon: '360', label: 'Step inside our completed spaces in 360°.',
          url: tourHref, page: null, has_children: false, children: [] },
      ] };
    }),
  };
});
</script>
<template>
  <header class="relative w-full mx-auto space-y-4 md:flex md:items-center md:space-y-0 md:gap-x-4" >
    <div class="flex items-center bg-white dark:bg-midnight-blue-900 justify-between py-2 px-6 md:flex-1 shadow-md">
      <NuxtLink :href="config.public.directus.rest.nuxtBaseUrl" class="py-2">
        <Logo class=""/>
        <span class="sr-only">{{ title }}</span>
      </NuxtLink>

      <nav class="hidden md:flex md:space-x-4 lg:space-x-6" aria-label="Global">
        <!-- <div v-for="item in navigation?.items" :key="item.id" :item="item">{{ item }}<br/></div> -->
        <NavigationMenuItem v-for="item in displayedNavigation?.items" :key="item.id" :item="item" />
      </nav>
      <div class="flex items-center justify-end shrink-0 space-x-2">
        <DarkModeToggle class="hidden text-midnight-blue-500 dark:text-white md:block hover:text-white" bg="dark" />
      </div>
    </div>

    <!-- <div class="hidden h-full gap-4 md:flex">
      <UButton to="/contact-us" color="primary" size="xl" class="px-2">Let's Talk</UButton>
      <UButton to="/portal" color="primary" variant="ghost" size="xl" class="px-2">Login</UButton>
    </div> -->
    <NavigationMobileMenu v-if="displayedNavigation" :navigation="displayedNavigation" />
  </header>
</template>
